import { Link } from "react-router-dom"
import type { BookedAppointment, ScreeningProgram } from "../hooks"
import { useBookingOffering } from "../hooks"
import { appointmentTypeCopy } from "../preferencesCopy"
import {
  Box,
  Button,
  Divider,
  GenderAffirmingCareIcon,
  IconWithBackground,
  SuccessIcon,
  Tile,
  Typography,
  dayjs
} from "@carrotfertility/carotene-core"
import React from "react"

type BookedPageProps = {
  program: ScreeningProgram
  // The appointment returned by the booking call — the source of the assigned provider and the
  // visit window. Null only if the screen is reached without a recorded booking (defensive).
  appointment: BookedAppointment | null
  // The offering the member booked; keys the offering lookup for the "Visit" line.
  offeringId?: string
}

// The booking confirmation ("You're booked") screen. Reached once the booking call succeeds; it
// summarizes the appointment and sends the member home. The confirmation email + calendar invite
// are server-side, and the join link is fetched just-in-time nearer the visit — neither is shown
// here (see MEM-719).
export function BookedPage({ program, appointment, offeringId }: BookedPageProps): JSX.Element {
  // Offering is already cached from the preferences screen; this resolves the booked offering
  // to its display title + duration for the "Visit" line.
  const offeringQuery = useBookingOffering(program)
  const bookedOffering = offeringQuery.data?.offerings.find((offering) => offering.id === offeringId)

  const detailRows = [
    appointment
      ? {
          label: "With",
          value: appointment.provider.credential
            ? `${appointment.provider.name}, ${appointment.provider.credential}`
            : appointment.provider.name
        }
      : null,
    appointment ? { label: "Date & time", value: dayjs(appointment.startTime).format("ddd, MMM D · h:mm A") } : null
  ].filter((row): row is { label: string; value: string } => row !== null)

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(theme.tokens.spacing.xl),
        maxInlineSize: 640,
        marginInline: "auto",
        textAlign: "center"
      })}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <IconWithBackground color="success" icon={SuccessIcon} />
      </Box>

      <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.xs) })}>
        <Typography variant="h2" component="h1">
          You&apos;re booked
        </Typography>
      </Box>
      <Tile
        variant="primary"
        sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}
      >
        <Box sx={(theme) => ({ display: "flex", alignItems: "center", gap: theme.spacing(theme.tokens.spacing.sm) })}>
          <IconWithBackground color="primary" icon={GenderAffirmingCareIcon} />
          <Typography variant="h3">
            {bookedOffering?.durationMinutes +
              " minute " +
              appointmentTypeCopy(bookedOffering?.appointmentType ?? "initial").title}
          </Typography>
        </Box>
        <Divider />
        {detailRows.map((row) => (
          <Box
            key={row.label}
            sx={(theme) => ({
              display: "flex",
              inlineSize: "100%",
              gap: theme.spacing(theme.tokens.spacing.md)
            })}
          >
            <Typography
              sx={(theme) => ({ textAlign: "start", color: theme.palette.text.tertiary, inlineSize: "40%" })}
              component="dt"
            >
              {row.label}
            </Typography>
            <Typography component="dd" sx={{ margin: 0, textAlign: "start" }}>
              {row.value}
            </Typography>
          </Box>
        ))}
      </Tile>
      <Typography variant="h3">Take a moment to prepare for your appointment.</Typography>
      <Typography variant="body1">
        We&apos;ll need your identification and a few details about your medical history prior to your appointment{" "}
      </Typography>
      <Box sx={(theme) => ({ display: "flex", gap: theme.spacing(theme.tokens.spacing.md) })}>
        <Button color="secondary" component={Link} to={"/"}>
          Complete forms
        </Button>
        <Button component={Link} variant="outlined" color="secondary" to={"/"}>
          Not now
        </Button>
      </Box>
    </Box>
  )
}
